/* DietIndex — the parent hub at `/diet` linking to all 11 dietary
 * requirement landing pages. Doubles as an SEO catch-all for
 * "special diet recipes" style searches and as a **combo filter** —
 * tick 2+ diets and hit "Find recipes" to see dishes that satisfy
 * every requirement at once (e.g. Vegan + Gluten-Free + Nut-Free). */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { ShieldCheck, Funnel, X, CheckCircle } from "@phosphor-icons/react";
import RecipeCard from "@/components/RecipeCard";

export default function DietIndex() {
  const [diets, setDiets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [combo, setCombo] = useState(null); // { total, recipes, labels }
  const [comboLoading, setComboLoading] = useState(false);

  useEffect(() => {
    api.get("/public/diets")
      .then((r) => setDiets(r.data?.diets || []))
      .catch(() => setDiets([]));
  }, []);

  const toggle = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
    // Combo results are stale as soon as the selection changes.
    setCombo(null);
  };

  const clearAll = () => { setSelected(new Set()); setCombo(null); };

  const runCombo = async () => {
    if (selected.size === 0) return;
    setComboLoading(true);
    try {
      const slugs = [...selected].join(",");
      const { data } = await api.get("/public/diet-combo", { params: { slugs } });
      setCombo(data);
    } catch (_) { /* ignore */ }
    finally { setComboLoading(false); }
  };

  const selectedLabels = useMemo(
    () => diets.filter((d) => selected.has(d.slug)).map((d) => d.label),
    [diets, selected],
  );

  // JSON-LD schemas rendered sync-with-DOM so Emergent's crawler-cache
  // headless snapshot captures them in the initial HTML — Google reads
  // CollectionPage + ItemList to understand this is a hub page and
  // BreadcrumbList to show the trail in search results.
  const jsonLd = useMemo(() => {
    if (!diets.length) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://recipefiler.com/" },
          { "@type": "ListItem", position: 2, name: "Special-diet recipes", item: "https://recipefiler.com/diet" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Special-diet recipes",
        url: "https://recipefiler.com/diet",
        description: "Recipes for every dietary requirement — Vegan, Vegetarian, Coeliac / Gluten-Free, Lactose / Dairy-Free, Egg Allergy, Nut-Free, Soy-Free, Low-FODMAP, Low-Histamine, Diabetic-Friendly and Renal-Friendly.",
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
        mainEntity: {
          "@type": "ItemList",
          name: "Dietary requirements covered",
          numberOfItems: diets.length,
          itemListElement: diets.map((d, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            url: `https://recipefiler.com/diet/${d.slug}`,
            name: d.label,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Can I combine multiple dietary requirements?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. Tick two or more requirements above and we only show recipes that satisfy all of them — e.g. Vegan + Gluten-Free + Nut-Free narrows the directory to dishes safe for every ticked diet.",
            },
          },
          {
            "@type": "Question",
            name: "Are the special-diet recipes checked for allergens?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Every recipe is drafted with a strict prohibited-ingredient spec per diet. We also surface the safety rules on each diet page so you can see exactly what's excluded. Always cross-check your own packet labels — manufacturer recipes change.",
            },
          },
        ],
      },
    ];
  }, [diets]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-20" data-testid="diet-index">
      <PageMeta
        title="Special-diet recipes — vegan, gluten-free, low-FODMAP & more"
        description="Recipes for every dietary requirement — Vegan, Vegetarian, Coeliac / Gluten-Free, Lactose / Dairy-Free, Egg Allergy, Nut-Free, Soy-Free, Low-FODMAP, Low-Histamine, Diabetic-Friendly and Renal-Friendly. Tick multiple diets to combine."
        keywords="special diet recipes, dietary requirement recipes, vegan recipes, vegetarian recipes, gluten-free recipes, dairy-free recipes, egg-free recipes, nut-free recipes, soy-free recipes, low FODMAP recipes, low histamine recipes, diabetic recipes, renal diet recipes, allergy-safe cooking, combine diet filters"
        canonical="/diet"
        jsonLd={jsonLd}
      />
      <p className="overline mb-3">Special-diet recipes</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">
        Cooking for a dietary need?
      </h1>
      <p className="text-rf-ink-soft text-lg mb-10 max-w-3xl">
        Every recipe here is drafted with strict safety rules — prohibited ingredients spelled out, safe swaps built in, and metric measurements throughout. Pick one to explore, or tick <b>two or more</b> to find recipes that satisfy every requirement.
      </p>

      {/* Combo filter panel */}
      <div className="rounded-2xl border border-rf-ink/10 bg-white p-5 mb-10" data-testid="diet-combo-panel">
        <div className="flex items-center gap-2 mb-3">
          <Funnel size={18} weight="duotone" className="text-rf-orange" />
          <h2 className="font-display text-lg font-black text-rf-ink">
            Combine dietary requirements
          </h2>
        </div>
        <p className="text-xs text-rf-ink-soft mb-4">
          Tick every requirement you need. Recipes are only shown when they satisfy <b>all</b> ticked diets.
        </p>
        {diets.length === 0 ? (
          <p className="text-sm text-rf-ink-soft">Loading dietary requirements…</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {diets.map((d) => {
                const active = selected.has(d.slug);
                return (
                  <button
                    key={d.slug}
                    type="button"
                    onClick={() => toggle(d.slug)}
                    data-testid={`diet-combo-toggle-${d.slug}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${
                      active
                        ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                        : "bg-rf-cream text-rf-ink-soft border-rf-ink/10 hover:bg-white hover:text-rf-ink"
                    }`}
                  >
                    {active && <CheckCircle size={12} weight="fill" className="text-emerald-700" />}
                    {d.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={runCombo}
                disabled={selected.size === 0 || comboLoading}
                data-testid="diet-combo-run"
                className="btn-amber !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {comboLoading
                  ? "Finding recipes…"
                  : selected.size === 0
                    ? "Tick one or more to search"
                    : `Find ${selected.size === 1 ? "" : "combo "}recipes (${selected.size} tick${selected.size === 1 ? "" : "s"})`}
              </button>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  data-testid="diet-combo-clear"
                  className="text-xs font-bold text-rf-ink-soft hover:text-red-600 inline-flex items-center gap-1"
                >
                  <X size={12} weight="bold" /> Clear
                </button>
              )}
            </div>
          </>
        )}

        {/* Combo results */}
        {combo && (
          <div className="mt-6 pt-6 border-t border-rf-ink/10" data-testid="diet-combo-results">
            <p className="text-sm text-rf-ink mb-4">
              <b>{combo.total}</b> {combo.total === 1 ? "recipe satisfies" : "recipes satisfy"} <span className="italic">{selectedLabels.join(" + ") || "your combo"}</span>.
            </p>
            {combo.recipes?.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {combo.recipes.map((r) => (<RecipeCard key={r.recipe_id} recipe={r} />))}
              </div>
            ) : (
              <p className="text-sm text-rf-ink-soft">No recipes match every requirement yet — try relaxing one.</p>
            )}
          </div>
        )}
      </div>

      {/* Individual diet cards */}
      <h2 className="font-display text-2xl font-black text-rf-ink mb-4">Or browse a single dietary requirement</h2>
      {diets.length === 0 ? (
        <p className="text-rf-ink-soft">Loading…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="diet-grid">
          {diets.map((d) => (
            <Link
              key={d.slug}
              to={`/diet/${d.slug}`}
              data-testid={`diet-card-${d.slug}`}
              className="block rounded-2xl border border-rf-ink/10 bg-white p-5 hover:border-rf-amber hover:bg-rf-amber/5 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={18} weight="duotone" className="text-emerald-600" />
                <span className="text-[10px] font-black uppercase tracking-wide text-rf-ink-soft">Diet</span>
              </div>
              <h3 className="font-display text-xl font-black text-rf-ink mb-1 group-hover:text-rf-orange">
                {d.label}
              </h3>
              <p className="text-xs text-rf-ink-soft line-clamp-3">
                Never includes: {d.prohibited.split(",").slice(0, 4).map((x) => x.trim()).join(", ")}…
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
